import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { UserCog, X, ChevronRight } from "lucide-react";

/**
 * 정회원(특히 레거시) 로그인 시 "상세정보(추가정보 양식)" 입력을 강하게 유도하는 상단 배너.
 * - 대상: 정회원 이상(백엔드 needs_detail_profile=true)
 * - 상세정보 완성 전까지 계속 노출 (세션 내 닫기 가능, 다음 로그인 시 다시 노출)
 * - 실명 입력(needs_real_name)이 우선 — 그 동안에는 숨김 (중복 안내 방지)
 */
export function ProfileCompletionPrompt() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setShow(false);
      return;
    }
    const isAdmin =
      user.is_admin ||
      user.role === "admin" ||
      (user.email || "").toLowerCase() === "admin@newcoupling.com";

    const needs = user.needs_detail_profile && !user.needs_real_name && !isAdmin;
    const dismissed = sessionStorage.getItem("nc_detailprofile_banner_dismissed");
    setShow(Boolean(needs) && !dismissed);
  }, [isAuthenticated, user]);

  const goEdit = () => {
    setShow(false);
    navigate("/profile/intro");
  };

  const dismiss = () => {
    sessionStorage.setItem("nc_detailprofile_banner_dismissed", "1");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed top-20 left-0 right-0 z-40 bg-burgundy-50 border-b border-burgundy-100 shadow-sm"
      data-testid="detail-profile-banner"
    >
      <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <UserCog className="w-4 h-4 text-burgundy-600 shrink-0" />
        <p className="text-[13px] text-burgundy-900 flex-1 leading-snug">
          <span className="font-semibold">정회원 상세정보(추가정보)를 모두 입력해 주세요.</span>{" "}
          <span className="text-burgundy-700/80">자세한 프로필을 채울수록 더 좋은 인연을 만날 수 있어요.</span>
        </p>
        <button
          onClick={goEdit}
          className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-burgundy-600 hover:bg-burgundy-700 text-white text-xs font-semibold transition-colors"
          data-testid="detail-profile-banner-edit"
        >
          상세정보 입력 <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={dismiss}
          className="shrink-0 p-1 text-burgundy-400 hover:text-burgundy-700"
          aria-label="닫기"
          data-testid="detail-profile-banner-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default ProfileCompletionPrompt;
